import {
  REVIEW_UNIT_SECTIONS,
  REVIEW_UNITS,
  SectionKeyType,
  SectionStatus,
  SectionStatusType
} from './data-qa-constants';
import {
  ChannelConfig,
  ChannelStats,
  LoadResult,
  WeekChannelTotals,
  WeekRow
} from './data-qa-types';
import { filterWeeksToFencepost, ModelGroup } from './data-qa-utils';
import { DivergenceSeverity, severityFor } from './divergence-ranges';
import {
  combineSectionStatus,
  divergenceReasons,
  IndicatorReason,
  metricRanges,
  ReviewIndicator,
  sortReasons
} from './media-inputs-utils';
import { periodUnitFor } from './negative-values-utils';

// MMM's total against BPM's over the same weeks: signed (MMM / BPM - 1) and the same
// note/investigate severity Media Inputs shades its charts with. Undefined when no week
// in the window has BPM data, or BPM's total is 0.
export interface BpmComparison {
  diff: number;
  severity?: DivergenceSeverity;
}

interface DeliverySummary {
  spend: number;
  impressions: number;
  spendVsBpm?: BpmComparison;
  impressionsVsBpm?: BpmComparison;
}

export interface PlatformSummaryRow extends DeliverySummary {
  key: string;
  label: string;
  // Week by week against BPM, like the channel's own indicator (empty when it's clear).
  reasons: IndicatorReason[];
}

export interface ChannelSummaryRow extends DeliverySummary {
  key: string;
  // The channel's name without a demand-group suffix; `group` says which side it's on.
  label: string;
  group: ChannelConfig['group'];
  // Platforms with any MMM or BPM delivery in the window, by spend, largest first.
  platforms: PlatformSummaryRow[];
}

interface FencepostWindow {
  fencepostStart?: string;
  fencepostEnd?: string;
  reviewGranularity?: string;
}

// Compared only over weeks where BPM has a value (undefined means no ground truth that
// week, not zero), so a partial BPM history doesn't read as a shortfall.
const compareToBpm = (
  weeks: WeekRow[],
  mmmOf: (w: WeekRow) => number,
  bpmOf: (w: WeekRow) => number | undefined
): BpmComparison | undefined => {
  const matched = weeks.filter(w => bpmOf(w) !== undefined);
  const bpm = matched.reduce((sum, w) => sum + (bpmOf(w) ?? 0), 0);
  if (matched.length === 0 || bpm === 0) return undefined;
  const mmm = matched.reduce((sum, w) => sum + mmmOf(w), 0);
  const diff = mmm / bpm - 1;
  return { diff, severity: severityFor(Math.abs(diff)) };
};

const summarize = (
  weeks: WeekRow[],
  totalsOf: (w: WeekRow) => WeekChannelTotals | undefined
): DeliverySummary => ({
  spend: weeks.reduce((sum, w) => sum + (totalsOf(w)?.spend ?? 0), 0),
  impressions: weeks.reduce((sum, w) => sum + (totalsOf(w)?.impressions ?? 0), 0),
  spendVsBpm: compareToBpm(
    weeks,
    w => totalsOf(w)?.spend ?? 0,
    w => totalsOf(w)?.bpmSpend
  ),
  impressionsVsBpm: compareToBpm(
    weeks,
    w => totalsOf(w)?.impressions ?? 0,
    w => totalsOf(w)?.bpmImpressions
  )
});

const hasDelivery = (weeks: WeekRow[], totalsOf: (w: WeekRow) => WeekChannelTotals | undefined) =>
  weeks.some(w => {
    const t = totalsOf(w);
    return Boolean(t?.spend || t?.impressions || t?.bpmSpend || t?.bpmImpressions);
  });

// A platform's own week-by-week check against BPM -- the same rule as its channel's
// indicator, so a platform row shows which platform turned the channel's dot yellow or red.
// Impressions count only when the channel's impressions are charted at all.
const platformReasons = (
  weeks: WeekRow[],
  totalsOf: (w: WeekRow) => WeekChannelTotals | undefined,
  hasImpressions: boolean,
  periodUnit: string
): IndicatorReason[] => {
  if (!weeks.some(w => totalsOf(w)?.bpmSpend !== undefined)) {
    return [{ indicator: ReviewIndicator.NO_BPM, text: 'No BPM data to compare against' }];
  }
  return sortReasons(
    divergenceReasons(
      [
        { metric: 'spend', ranges: metricRanges(weeks, totalsOf, 'spend') },
        ...(hasImpressions
          ? [
              {
                metric: 'impressions' as const,
                ranges: metricRanges(weeks, totalsOf, 'impressions')
              }
            ]
          : [])
      ],
      weeks,
      periodUnit
    )
  );
};

// One row per channel entry (a channel split across Demand Generation and Demand Capture
// is two), with its spend and impressions over the modeling range and how they compare
// with BPM's, and the same for each of its platforms.
export const buildChannelSummaryRows = (
  channels: ChannelConfig[],
  weeks: WeekRow[],
  channelStats: ChannelStats[],
  { fencepostStart, fencepostEnd, reviewGranularity }: FencepostWindow
): ChannelSummaryRow[] => {
  const windowWeeks = filterWeeksToFencepost(
    weeks,
    fencepostStart,
    fencepostEnd,
    reviewGranularity
  );
  const periodUnit = periodUnitFor(reviewGranularity);
  return channels.map(channel => {
    const totalsOf = (w: WeekRow) => w.perChannel[channel.key];
    const hasImpressions =
      (channelStats.find(c => c.channelKey === channel.key)?.avgImpressions ?? 0) > 0;
    const platforms = channel.platforms
      .map(platform => {
        const platformTotalsOf = (w: WeekRow) => totalsOf(w)?.perPlatform?.[platform.key];
        return { platform, platformTotalsOf };
      })
      .filter(({ platformTotalsOf }) => hasDelivery(windowWeeks, platformTotalsOf))
      .map(({ platform, platformTotalsOf }) => ({
        key: platform.key,
        label: platform.label,
        ...summarize(windowWeeks, platformTotalsOf),
        reasons: platformReasons(windowWeeks, platformTotalsOf, hasImpressions, periodUnit)
      }))
      .sort((a, b) => b.spend - a.spend);
    return {
      key: channel.key,
      label: channel.rawLabel,
      group: channel.group,
      ...summarize(windowWeeks, totalsOf),
      platforms
    };
  });
};

export interface ModelTarget {
  label: string;
  // A total over the modeling range, or -- for a brand-health index, which doesn't add
  // up across weeks -- its average. Undefined while the model is still loading.
  value?: number;
  aggregation: 'total' | 'average';
  // A revenue metric, shown in dollars.
  currency: boolean;
  // First and last period of the modeling range. Undefined while the model is still
  // loading, or when it has no data in its window.
  windowStart?: string;
  windowEnd?: string;
}

// Revenue targets are dollars. Other targets (search volume, orders, brand-health
// indexes) are plain counts or scores, so they stay unformatted.
export const isRevenueMetric = (...names: (string | undefined)[]): boolean =>
  names.some(name => /revenue/i.test(name ?? ''));

export interface ModelReviewProgress {
  approved: number;
  flagged: number;
  total: number;
}

export interface ModelOutcomeRow {
  // The config to switch to: the active one in this group, else its primary.
  configPath: string;
  productType: ModelGroup['productType'];
  label: string;
  active: boolean;
  // One per stage (Brand Equity has two).
  targets: ModelTarget[];
  // Undefined while the model is still loading and has no review in this session.
  progress?: ModelReviewProgress;
}

const targetOf = (load: LoadResult): ModelTarget => {
  const { config, dataset } = load;
  const windowWeeks = filterWeeksToFencepost(
    dataset.weeks,
    config.fencepostStart,
    config.fencepostEnd,
    dataset.reviewGranularity
  );
  const base = {
    label: config.dependentVarLabel,
    currency: isRevenueMetric(config.dependentVarLabel, config.dependentVar),
    windowStart: windowWeeks[0]?.weekIso,
    windowEnd: windowWeeks[windowWeeks.length - 1]?.weekIso
  };
  if (config.brandHealthVars?.length) {
    const stats =
      dataset.dependentVarStats.find(s => s.key === config.dependentVar) ??
      dataset.dependentVarStats[0];
    return { ...base, value: stats?.mean, aggregation: 'average' };
  }
  return {
    ...base,
    value: windowWeeks.reduce((sum, w) => sum + w.dependentValue, 0),
    aggregation: 'total'
  };
};

// How many of a model's review units (Media Inputs, Context Variables, Dependent Variable)
// are approved or flagged, from its section statuses.
export const reviewProgress = (
  sectionStatus: Record<SectionKeyType, SectionStatusType>
): ModelReviewProgress => {
  const statuses = REVIEW_UNITS.map(unit =>
    combineSectionStatus(REVIEW_UNIT_SECTIONS[unit].map(section => sectionStatus[section]))
  );
  return {
    approved: statuses.filter(s => s === SectionStatus.APPROVED).length,
    flagged: statuses.filter(s => s === SectionStatus.FLAGGED).length,
    total: statuses.length
  };
};

// One row per model: its target metric(s) over the modeling range and its review
// progress. `loadByPath` holds whichever models have loaded so far; `statusFor` gives a
// config's current section statuses (this session's, else its last saved review), if
// known. Progress comes from the model's primary config.
export const buildModelOutcomeRows = (
  groups: ModelGroup[],
  activeConfigPath: string | undefined,
  loadByPath: Map<string, LoadResult>,
  statusFor: (configPath: string) => Record<SectionKeyType, SectionStatusType> | undefined
): ModelOutcomeRow[] =>
  groups.map(group => {
    const activeConfig = group.configs.find(c => c.configPath === activeConfigPath);
    const primary = activeConfig ?? group.configs[0];
    const targets = group.configs.map(config => {
      const load = loadByPath.get(config.configPath);
      return load
        ? targetOf(load)
        : {
            label: config.dependentVarLabel ?? config.dependentVar ?? '',
            aggregation: 'total' as const,
            currency: isRevenueMetric(config.dependentVarLabel, config.dependentVar)
          };
    });
    const status = statusFor(group.configs[0].configPath);
    return {
      configPath: primary.configPath,
      productType: group.productType,
      label: group.label,
      active: Boolean(activeConfig),
      targets,
      progress: status ? reviewProgress(status) : undefined
    };
  });
